/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component } from '@angular/core';
import { Validators } from '@angular/forms';
import {
    Consts, DMValidators, EventService, FormControl, FormGroup, HttpService, I18nService, LazyloadService,
    PermissionService, UNPLACEHOLDER,
    UtilService,
} from '@dm/core';
import { LicenseKey } from 'app/common/constants/license-key';
import { ToolService } from 'app/common/services';
import { type GuaranteedCapacity } from 'app/common/types/mo';
import { type OEM } from 'app/common/types/oem';
import { SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { type Observable } from 'rxjs';

@Component({
    selector: 'app-system-info',
    templateUrl: './system-info.component.html',
    styleUrls: ['./system-info.component.scss'],
})
export class SystemInfoComponent implements OnInit {
    oem: OEM = (window as any).oem;

    i18n = {
        systemInfoText: this.i18nService.get('common.device_info_label'),
        closeBtn: this.i18nService.get('common.close_button'),

        nameLab: this.i18nService.get('system.name_column'),
        modelLab: this.i18nService.get('common.device_model_label'),
        modelName: this.i18nService.get('system.hardware_term_diskMode_label'),
        locationLab: this.i18nService.get('system.location_column'),
        versionLab: this.i18nService.get('common.device_version_label'),
        patchVersionLab: this.i18nService.get('home.device_term_patchVersion_lab'),
        snLab: this.i18nService.get('common.device_ESN_label'),
        wwnLab: this.i18nService.get('common.device_WWN_label'),
        diskCapacityLab: this.i18nService.get('home.device_view_totleDiskCpa_lab'),
        capacityLab: this.i18nService.get('common.capacity_column'),
        guaranteedCapacityLab: this.i18nService.get('home.guarantCap_term_tittle'),
        totalCapacityLab: this.i18nService.get('common.totalCapacity_column'),
        insufficientThresholdLab: this.i18nService.get('home.guarantCap_term_highAlarmThreshold_label') + ' ' + '(%)',
        usedUpThresholdLab: this.i18nService.get('home.guarantCap_term_lowAlarmThreshold_label') + ' ' + '(%)',
        guaranteedCapacityCompareNumErr: this.i18nService.get('home.guarantCap_term_AlarmThreshold_info_label'),

        typeLab: this.i18nService.get('service.filesystem_task_create_worm_para_mode_label'),
        directModeLab: this.i18nService.get('common.directMode_value'),
        poolModeLab: this.i18nService.get('common.poolMode_value'),
        availableCapLab: this.i18nService.get('common.poolAvailableCapacityMode_value'),
        mediaTypeLab: this.i18nService.get('common.mediumType_lab'),
        hybridFlashLab: this.i18nService.get('common.mediumType_hybridFlash_value'),
        allFlashLab: this.i18nService.get('common.mediumType_allFlash_value'),

        copyBtn: this.i18nService.get('common.copy_button'),
    };

    pageReady = true;
    windowVisible = true;

    vm: FormGroup;
    errorMessage: any;

    isEditName = false;
    isEditLocation = false;
    isEditInsufficientThreshold = false;
    isEditUsedUpThreshold = false;

    supportGuaranteedCapacity = false;
    supportGeneralGuranteedCapacity = false;
    supportDedupeAndCompression = false;
    hasReadGuaranteedCapPermission = false;
    hasWriteGuaranteedCapPermission = false;
    guaranteedCapacity = '';

    UNPLACEHOLDER = UNPLACEHOLDER;
    SWITCH = SWITCH;
    FeatureConfig = FeatureConfig;

    valueForShowMode: string = this.getValueForShowMode();

    getValueForShowMode(): string {
        if (SWITCH.IS_DIRECT_MODE) {
            return this.i18n.directModeLab;
        } else if (SWITCH.IS_AvailableCap_MODE) {
            return this.i18n.availableCapLab;
        } else {
            return this.i18n.poolModeLab;
        }
    }

    hasSystemW: boolean;

    get isEditing(): boolean {
        return this.isEditName || this.isEditLocation || this.isEditInsufficientThreshold || this.isEditUsedUpThreshold;
    }

    constructor(
        private i18nService: I18nService,
        private httpService: HttpService,
        private utilService: UtilService,
        private lazyLoadService: LazyloadService,
        public toolService: ToolService,
        private permissionService: PermissionService,
        private eventService: EventService,
    ) { }

    ngOnInit(): void {
        this.hasSystemW = this.permissionService.hasPermission('system_W');
        this.hasReadGuaranteedCapPermission = this.permissionService.hasPermission('storage_pool_R');
        this.hasWriteGuaranteedCapPermission = this.permissionService.hasPermission('storage_pool_W');
        this.supportGuaranteedCapacity = this.permissionService.isLicenseExist(
            [[LicenseKey.GuaranteedCapacity, LicenseKey.SpecialLicense]]);
        this.supportGeneralGuranteedCapacity = this.permissionService.isLicenseExist(
            [LicenseKey.GuaranteedCapacity]);
        this.supportDedupeAndCompression = this.toolService.supportDedupeAndCompression();

        this.vm = new FormGroup({
            name: new FormControl('', [Validators.required, DMValidators.rangeByteLength(1, 127), DMValidators.commonName]),
            model: new FormControl(''),
            location: new FormControl('', [Validators.required, DMValidators.rangeByteLength(1, 511)]),
            version: new FormControl(''),
            sn: new FormControl(''),
            wwn: new FormControl(''),
            totalCapacity: new FormControl(''),
            threshold: new FormGroup({
                insufficientThreshold: new FormControl(''),
                usedUpThreshold: new FormControl(''),
            }, [DMValidators.compareNum()]),
        }, { updateOn: 'blur' });

        this.errorMessage = {
            name: {
                required: this.i18nService.get('common.null_validation', [this.i18n.nameLab]),
                commonName: this.i18nService.get('common.nameformat_validation', [this.i18n.nameLab]),
                rangeByteLength: this.i18nService.get('common.length1_msg', [this.i18n.nameLab, 1, 127]),
            },
            location: {
                required: this.i18nService.get('common.null_validation', [this.i18n.locationLab]),
                rangeByteLength: this.i18nService.get('common.length1_msg', [this.i18n.locationLab, 1, 511]),
            },
            threshold: {
                insufficientThreshold: {
                    compareNum: this.i18n.guaranteedCapacityCompareNumErr,
                },
                usedUpThreshold: {
                    compareNum: this.i18n.guaranteedCapacityCompareNumErr,
                },
            },
        };

        this.initDeviceInfo();
        // 微存储屏蔽可得容量
        if (!FeatureConfig.IsHideGuaranteedCapacity && this.supportGuaranteedCapacity && this.hasReadGuaranteedCapPermission) {
            this.initGuaranteedCapacity();
        } else {
            this.vm.get('threshold').disable();
        }
    }

    // 数字范围提示
    rangeTip = (min: number, max: number): string => this.i18nService.get('common.valueRange_label', [min, max]);

    initDeviceInfo(): void {
        this.httpService.get(this.httpService.makeUrl('system.get'), { mask: true })
            .subscribe(({ data }) => {
                const vm = this.utilService.moToVm(data, moMapping);
                vm.model = this.oem.vendor === 'huawei' ? this.toolService.getProductModelName(Number(vm.model)) : this.oem.productModel;
                vm.location = this.utilService.decodeHTML(vm.location);
                vm.totalCapacity = this.utilService.adaptUnit(data.TOTALCAPACITY * data.SECTORSIZE, Consts.BYTE).label;
                vm.version = data?.SoftwarePatch ?? data?.pointRelease;
                this.vm.patchValue(vm);
                this.vm.restore();
            });
    }

    initGuaranteedCapacity(): void {
        const rest = this.httpService.makeUrl('guaranteedCapacity.get');

        // 支持重删压缩License时，接口提示信息更换为“查询系统容量”
        if (this.supportDedupeAndCompression) {
            rest.name = 'getSystemCpa_msg';
        }

        this.httpService.get(rest, { mask: true })
            .subscribe((res) => {
                const data = res.data as GuaranteedCapacity;
                this.guaranteedCapacity = this.toolService.isInvalidNum(data.totalEffectiveCapacity, true) ?
                    UNPLACEHOLDER :
                    this.utilService.adaptUnit(data.totalEffectiveCapacity, Consts.SECTOR).label;
                this.vm.patchValue({
                    threshold: {
                        insufficientThreshold: data.insufficientThreshold,
                        usedUpThreshold: data.usedUpThreshold,
                    },
                });
                this.vm.restore();
            });
    }

    cancelEdit(): void {
        this.isEditName = false;
        this.isEditLocation = false;
        this.isEditInsufficientThreshold = false;
        this.isEditUsedUpThreshold = false;
        this.vm.resetToLast();
    }

    saveDeviceInfo(): void {
        let ob$: Observable<boolean>;

        if (this.isEditName) {
            ob$ = (this.vm.get('name') as FormControl).validateOb();
        } else {
            ob$ = (this.vm.get('location') as FormControl).validateOb();
        }

        ob$.subscribe((valid) => {
            if (!valid) {
                return;
            }

            const params = this.utilService.getChangedValue(this.vm.value, this.vm.lastValue, moMapping);

            if (!params) {
                this.isEditName = false;
                this.isEditLocation = false;
                return;
            }

            this.httpService.put(
                this.httpService.makeUrl('system.put'),
                params,
                {
                    mask: true,
                    doSuccess: true,
                },
            )
                .subscribe(() => {
                    this.isEditName = false;
                    this.isEditLocation = false;
                    this.vm.restore();
                });
        });
    }

    saveGuaranteedCapacity(): void {
        if (!this.hasWriteGuaranteedCapPermission) {
            return;
        }

        (this.vm.get('threshold') as FormGroup).validate((valid) => {
            if (!valid) {
                return;
            }

            const params = (this.vm.get('threshold') as FormGroup).changedRawValue;

            if (!params) {
                this.isEditInsufficientThreshold = false;
                this.isEditUsedUpThreshold = false;
                return;
            }

            this.httpService.put(
                this.httpService.makeUrl('guaranteedCapacity.put'),
                params,
                {
                    mask: true,
                    doSuccess: true,
                },
            )
                .subscribe(() => {
                    this.isEditInsufficientThreshold = false;
                    this.isEditUsedUpThreshold = false;
                    this.vm.restore();
                    this.eventService.emit('guaranteedCapacity.update');
                });
        });
    }

    close(): void {
        this.lazyLoadService.destroySelf(this);
    }
}

enum moMapping {
    name = 'NAME',
    model = 'PRODUCTMODE',
    location = 'LOCATION',
    version = 'pointRelease',
    totalCapacity = 'TOTALCAPACITY',
    sn = 'ID',
    wwn = 'wwn'
}
